import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Check, Shield, Zap, Loader2, X } from "lucide-react";

import backIcon from "..//assets/res/back_icon.svg";
import appleIcon from "..//assets/res/apple_white.svg";
import downloadIcon from "..//assets/res/download_icon.svg";
import androidIcon from "..//assets/res/android_white.svg";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { GET_BASE_URL } from "../utils/Constants";
import { slugify } from "../utils/Helpers";
import OffersScreen from "./OffersScreen";
import ViewerCount from "../components/ViewerCount";

const SITE_URL = "https://www.infinitemod.com";

// ── Inline prepare notification ───────────────────────────────────────────────
const PREPARE_STEPS = [
  { title: "Verifying connection", description: "Checking your device and network...", icon: Shield, duration: 3000 },
  { title: "Preparing your file", description: "Securing your download link...", icon: Zap, duration: 3000 },
];

const PrepareNotification = ({ gameName, imageUrl, onComplete, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  useEffect(() => {
    if (currentStep >= PREPARE_STEPS.length) {
      setTimeout(() => onCompleteRef.current(), 400);
      return;
    }
    const step = PREPARE_STEPS[currentStep];
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / step.duration) * 100, 100);
      setProgress(newProgress);
      if (newProgress >= 100) {
        clearInterval(interval);
        setTimeout(() => { setCurrentStep(s => s + 1); setProgress(0); }, 300);
      }
    }, 50);
    return () => clearInterval(interval);
  }, [currentStep]);

  const overallProgress = ((currentStep / PREPARE_STEPS.length) * 100);

  return (
    <>
      <style>{`
        @keyframes prepareSlideUp {
          from { transform: translateY(-100%); opacity: 0; }
          to   { transform: translateY(0);     opacity: 1; }
        }
        @keyframes prepareIconGlow {
          0%,100% { box-shadow: 0 0 10px rgba(70,143,175,0.35); }
          50%     { box-shadow: 0 0 22px rgba(70,143,175,0.65); }
        }
        @keyframes prepareShimmer {
          0%   { background-position: -200% center; }
          100% { background-position:  200% center; }
        }
        @keyframes borderFlow {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>

      {/* Backdrop */}
      <div
        className="absolute inset-0 z-[1300]"
        style={{ background: "rgba(0,8,18,0.6)", backdropFilter: "blur(4px)" }}
      />

      {/* Card */}
      <div
        className="absolute top-0 left-0 right-0 z-[1301] px-3 pt-4"
        style={{ animation: "prepareSlideUp 0.45s cubic-bezier(0,0,0.2,1) forwards" }}
      >
        <div
          className="relative overflow-hidden rounded-3xl p-5"
          style={{
            background: "linear-gradient(180deg, #010f1e 0%, #012a4a 100%)",
            border: "1px solid rgba(70,143,175,0.22)",
            boxShadow: "0 -8px 40px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.03) inset",
          }}
        >
          {/* Top edge highlight */}
          <div className="absolute top-0 left-8 right-8 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(70,143,175,0.5), transparent)" }} />

          {/* Header row: game info + dismiss */}
          <div className="flex items-center gap-3 mb-5">
            <img
              src={GET_BASE_URL() + "images/" + imageUrl}
              alt={gameName}
              className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
              style={{ border: "1px solid rgba(255,255,255,0.12)" }}
            />
            <div className="flex-1 min-w-0">
              <div className="semiBold text-white leading-tight truncate" style={{ fontSize: "15px" }}>{gameName}</div>
              <div className="medium" style={{ fontSize: "12px", color: "rgba(255,255,255,0.4)" }}>Preparing your download…</div>
            </div>
            <button
              onClick={onCancel}
              className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.1)" }}
            >
              <X className="w-3.5 h-3.5" style={{ color: "rgba(255,255,255,0.45)" }} />
            </button>
          </div>

          {/* Steps */}
          <div className="flex flex-col gap-3 mb-5">
            {PREPARE_STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isDone = index < currentStep;

              return (
                <div key={index} className="flex items-center gap-3">
                  {/* Icon */}
                  <div
                    className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-500"
                    style={
                      isDone ? {
                        background: "linear-gradient(135deg, #15803d, #16a34a)",
                        boxShadow: "0 3px 12px rgba(22,163,74,0.35)",
                      } : isActive ? {
                        background: "rgba(70,143,175,0.18)",
                        border: "1px solid rgba(70,143,175,0.4)",
                        animation: "prepareIconGlow 2s ease-in-out infinite",
                      } : {
                        background: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.07)",
                      }
                    }
                  >
                    {isDone ? (
                      <Check className="w-4 h-4 text-white" />
                    ) : isActive ? (
                      <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#89d4f5" }} />
                    ) : (
                      <Icon className="w-4 h-4" style={{ color: "rgba(255,255,255,0.2)" }} />
                    )}
                  </div>

                  {/* Title + bar */}
                  <div className="flex-1 min-w-0">
                    <div
                      className="semiBold leading-tight"
                      style={{ fontSize: "13px", color: (isActive || isDone) ? "#fff" : "rgba(255,255,255,0.3)" }}
                    >
                      {step.title}
                    </div>
                    {isActive && (
                      <div className="mt-1.5 rounded-full overflow-hidden" style={{ height: "3px", background: "rgba(255,255,255,0.08)" }}>
                        <div
                          style={{
                            height: "100%",
                            width: `${progress}%`,
                            background: "linear-gradient(90deg, #01497c, #468faf, #89d4f5)",
                            backgroundSize: "200% 100%",
                            animation: "prepareShimmer 1.8s linear infinite",
                            boxShadow: "0 0 6px rgba(70,143,175,0.6)",
                            transition: "width 0.05s linear",
                          }}
                        />
                      </div>
                    )}
                    {isDone && (
                      <div className="mt-1.5 rounded-full overflow-hidden" style={{ height: "3px", background: "rgba(255,255,255,0.08)" }}>
                        <div className="h-full w-full rounded-full" style={{ background: "linear-gradient(90deg, #15803d, #22c55e)" }} />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Overall progress bar */}
          <div
            className="rounded-xl px-3 py-2.5"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <span className="medium" style={{ fontSize: "11px", color: "rgba(255,255,255,0.4)" }}>Overall</span>
              <span className="semiBold text-white" style={{ fontSize: "11px" }}>{currentStep} / {PREPARE_STEPS.length}</span>
            </div>
            <div className="rounded-full overflow-hidden" style={{ height: "3px", background: "rgba(255,255,255,0.08)" }}>
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${overallProgress}%`,
                  background: "linear-gradient(90deg, #01497c, #468faf)",
                  boxShadow: "0 0 6px rgba(70,143,175,0.5)",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const detectPlatform = () => {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  return "android";
};

// ── Community / Reviews data ──────────────────────────────────────────────

const COMMENT_TEMPLATES = [
  "Easy to install, {name} worked perfectly on my device",
  "Thanks for {name}, exactly what I was looking for",
  "Been playing {name} for hours, the mod is legit",
  "Finally a working version of {name}!",
  "{name} runs smooth, no lag at all",
  "The mod features in {name} are insane, love it",
  "Took 2 minutes to set up {name}, great instructions",
  "Never thought I'd find {name} for free, amazing",
  "Downloaded {name} and it works flawlessly",
  "{name} is exactly what was advertised, no bugs",
  "5 stars for {name}, best mod out there",
  "Highly recommend {name} to anyone looking for mods",
  "The graphics in {name} look even better with this mod",
  "Couldn't believe {name} was free, absolutely worth it",
  "My friends are jealous I have the {name} mod",
  "Fast download, {name} installed without any issues",
  "Been waiting for a mod like {name} for so long",
  "{name} changed how I play completely",
  "The unlimited resources in {name} are a game changer",
  "Just tried {name} and I'm already addicted",
  "No ads in {name} is the best part of this mod",
  "Legit and working, {name} is the real deal",
  "Shared {name} with my whole group chat",
  "Instructions were clear and {name} works great",
  "Best version of {name} I've found anywhere online",
  "{name} mod has all the features I wanted",
  "Smooth gameplay after installing {name}",
  "So glad I found this site for {name}",
  "The {name} mod is completely safe and virus free",
  "Tested {name} on 3 devices, all working perfectly",
  "Downloading {name} was the best decision I made today",
  "Love the unlimited coins in {name}, makes it so fun",
  "{name} runs better than the original version",
  "Thank you for keeping {name} updated",
  "Got {name} working on Android 14, no issues",
  "The premium features in {name} are unlocked perfectly",
  "Clean install of {name}, no extra steps needed",
  "Appreciate the devs for keeping {name} free",
  "{name} is the only mod I trust from this site",
  "Incredible mod, {name} exceeded my expectations",
  "Installed {name} in under 5 minutes, so easy",
  "This is the best source for {name} mods for sure",
  "The {name} mod gives you everything the paid version has",
  "Zero crashes after a week of playing {name}",
  "My whole family plays {name} now because of this mod",
  "Brilliant work on {name}, keep it up",
  "{name} works on my old phone too, impressive",
  "The mod menu in {name} is super clean and easy to use",
  "Been using this site for {name} for months, always reliable",
  "Can't play without the mod version of {name} anymore",
  "This {name} mod is the only one that actually works",
  "Verified working on iOS and Android, {name} is flawless",
  "Unlimited everything in {name} makes it way more enjoyable",
  "Never going back to the original after this {name} mod",
  "Solid performance after installing {name} on my device",
  "The team behind {name} mods deserves recognition",
  "Trusted site, {name} downloaded without any problems",
  "{name} is now my favourite game thanks to this mod",
  "Simple steps and {name} was ready in minutes",
  "This mod of {name} has everything and more",
  "Appreciate how regularly {name} gets updated here",
  "Really impressed with the {name} mod quality",
  "No root needed for {name}, that's a big plus",
  "Safe and clean mod for {name}, highly recommend",
  "{name} is so much better with the mod enabled",
  "Love that {name} is available for both platforms",
  "Perfect mod for {name}, not a single problem",
  "Genuinely surprised at how well {name} works",
  "This site always comes through with {name} mods",
  "Thanks to everyone who made {name} available for free",
];

const COMMENTER_NAMES = [
  "Jake M.", "gamer_pro99", "Sarah K.", "Anonymous", "xXDragonSlayerXx",
  "TechUser42", "Mike", "Luna_Games", "speedrunner07", "Anonymous",
  "CoolDude88", "Maria L.", "GamingKing", "player_one", "Alex T.",
  "shadow_mod", "Emma R.", "NoobSlayer", "Anonymous", "TopGamer",
  "Chris B.", "modlover22", "Priya S.", "RetroPlayer", "Anonymous",
  "Max_Power", "JennyG", "ultra_instinct", "DarkHorse99", "Sam W.",
];

const AVATAR_COLORS = [
  "#1d4ed8", "#7c3aed", "#b45309", "#065f46", "#9f1239", "#0e7490",
];

function seededRandom(seed) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = Math.imul(31, h) + seed.charCodeAt(i) | 0;
  return () => {
    h ^= h << 13; h ^= h >> 17; h ^= h << 5;
    return (h >>> 0) / 4294967296;
  };
}

function generateComments(gameName, count = 7) {
  const rand = seededRandom(gameName);
  const now = Date.now();
  const sixMonthsMs = 6 * 30 * 24 * 60 * 60 * 1000;
  const usedTemplates = new Set();
  const comments = [];

  while (comments.length < count) {
    const tIdx = Math.floor(rand() * COMMENT_TEMPLATES.length);
    if (usedTemplates.has(tIdx)) continue;
    usedTemplates.add(tIdx);

    const author = COMMENTER_NAMES[Math.floor(rand() * COMMENTER_NAMES.length)];
    const daysAgo = Math.floor(rand() * 180);
    const date = new Date(now - daysAgo * 86400000);
    const dateStr = daysAgo === 0
      ? "Today"
      : daysAgo === 1
      ? "Yesterday"
      : daysAgo < 7
      ? `${daysAgo} days ago`
      : daysAgo < 30
      ? `${Math.floor(daysAgo / 7)} week${Math.floor(daysAgo / 7) > 1 ? "s" : ""} ago`
      : date.toLocaleDateString("en-US", { month: "short", day: "numeric" });

    const starsPool = [3, 4, 4, 4, 5, 5, 5, 5];
    const stars = starsPool[Math.floor(rand() * starsPool.length)];

    comments.push({
      text: COMMENT_TEMPLATES[tIdx].replace(/{name}/g, gameName.toLowerCase()),
      author,
      date: dateStr,
      stars,
    });
  }
  return comments;
}

const StarRating = ({ stars }) => (
  <span style={{ fontSize: "12px", letterSpacing: "1px" }}>
    {[1,2,3,4,5].map(i => (
      <span key={i} style={{ color: i <= stars ? "#f59e0b" : "rgba(255,255,255,0.2)" }}>★</span>
    ))}
  </span>
);

const avatarColor = (name) => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

// ─────────────────────────────────────────────────────────────────────────────

const StatPill = ({ label, children }) => (
  <div
    className="flex-shrink-0 rounded-2xl py-3 px-2 flex flex-col items-center justify-center"
    style={{
      width: "96px",
      background: "rgba(255,255,255,0.05)",
      border: "1px solid rgba(255,255,255,0.08)",
    }}
  >
    <span className="text-gray-400 medium text-center" style={{ fontSize: "10px", letterSpacing: "0.06em" }}>
      {label}
    </span>
    <div className="mt-1">{children}</div>
  </div>
);

const DetailsPage = ({ fromPage, selectedGame, handleClose, numbers }) => {
  const [showOffers, setShowOffers] = useState(false);
  const [showPreparing, setShowPreparing] = useState(false);
  const [platform, setPlatform] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [commentsExpanded, setCommentsExpanded] = useState(false);

  if (!selectedGame) return;

  const {
    name,
    description,
    image_path,
    rate,
    size,
    author,
    download,
    isForAndroid,
    isForIos,
    gameKeywords,
    category,
  } = selectedGame;

  const heroUrl = GET_BASE_URL() + "images/" + image_path;

  // SEO: per-game canonical URL, OG image and structured data.
  const isApp = category === "2";
  const canonicalUrl = `${SITE_URL}/${isApp ? "app" : "game"}/${slugify(name)}`;
  // Share/OG image on the canonical host (visible <img> below uses heroUrl).
  const ogImage = `${SITE_URL}/images/${image_path}`;
  const operatingSystem = [isForAndroid && "ANDROID", isForIos && "IOS"]
    .filter(Boolean)
    .join(", ");
  const ratingCount = Number(download) > 0 ? Math.round(Number(download)) : 1000;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name,
    description,
    image: ogImage,
    url: canonicalUrl,
    applicationCategory: isApp ? "MobileApplication" : "GameApplication",
    operatingSystem: operatingSystem || "ANDROID, IOS",
    author: author ? { "@type": "Organization", name: author } : undefined,
    aggregateRating: rate
      ? {
          "@type": "AggregateRating",
          ratingValue: rate,
          ratingCount,
          bestRating: "5",
          worstRating: "1",
        }
      : undefined,
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  };
  const commentCount = 7 + (seededRandom(name + "_count")() * 9 | 0); // 7–15, consistent per game
  const allComments = generateComments(name, commentCount);
  const visibleComments = commentsExpanded ? allComments : allComments.slice(0, 3);

  function handleDownload() {
    setPlatform(detectPlatform());
    setShowPreparing(true);
  }

  return (
    <>
      <Helmet>
        <title>{`${name} Mod ${isApp ? "APK" : "(APK/IPA)"} — Free Download | Infinite Mod`}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={gameKeywords} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Infinite Mod" />
        <meta property="og:title" content={`${name} Mod — Free Download`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${name} Mod — Free Download`} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="flex flex-col bg-semi-blue min-h-full relative">

        {/* ── Hero banner ── */}
        {/* Outer has NO overflow-hidden so the icon can bleed below */}
        <div className="relative w-full flex-shrink-0" style={{ height: "176px" }}>
          {/* Background layer — clipped to hero height */}
          <div className="absolute inset-0 overflow-hidden">
            <img
              src={heroUrl}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover"
              style={{
                filter: "blur(16px) brightness(0.5) saturate(1.4)",
                transform: "scale(1.18)",
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(0,10,25,0.25) 0%, rgba(1,42,74,0.55) 55%, #013a63 100%)",
              }}
            />
          </div>

          {/* Back button — top left */}
          <div
            className="absolute top-4 left-4 flex flex-row items-center gap-1 cursor-pointer z-10"
            onClick={handleClose}
          >
            <img className="h-7 w-7" src={backIcon} alt="Back" />
            <span className="text-white semiBold text-lg pt-0.5 opacity-85">{fromPage}</span>
          </div>

          {/* Game icon — centered, overlaps below hero */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-[35%] z-10">
            <div
              className="h-28 w-28 rounded-3xl overflow-hidden"
              style={{
                border: "2.5px solid rgba(255,255,255,0.2)",
                boxShadow: "0 12px 40px rgba(0,0,0,0.75), 0 0 0 5px rgba(1,42,74,0.7)",
              }}
            >
              <img
                className="h-28 w-28 object-cover object-center"
                src={heroUrl}
                alt={name}
              />
            </div>
          </div>
        </div>

        {/* ── Content (below hero) ── */}
        <div className="flex flex-col px-4 pt-14 pb-8">

          {/* Title */}
          <div className="text-white semiBold text-2xl text-center line-clamp-2 leading-tight">
            {name}
          </div>

          {/* Download button */}
          <div className="relative mt-4 active:scale-[0.97] transition-transform duration-150">
            <div
              className="absolute -inset-[2px] rounded-full opacity-90"
              style={{
                background: "linear-gradient(135deg, #ca8a04, #ea580c, #dc2626, #468faf, #ca8a04)",
                backgroundSize: "300% 300%",
                animation: "borderFlow 2.5s ease infinite",
                filter: "blur(2px)",
              }}
            />
            <button
              className="relative w-full flex flex-row items-center justify-center gap-2 rounded-full bg-dark-blue py-3 text-white semiBold"
              style={{ fontSize: "17px", letterSpacing: "0.03em" }}
              onClick={handleDownload}
            >
              Download
              <img src={downloadIcon} className="h-5 w-5" alt="" />
            </button>
          </div>

          {/* Free note */}
          <div className="flex flex-row items-center justify-center gap-1.5 mt-3">
            <span className="text-green-400 text-xs medium">✓ Free</span>
            <span className="text-gray-500 text-xs">·</span>
            <span className="text-gray-400 text-xs medium">Complete a quick offer to unlock</span>
          </div>

          <div className="flex justify-center">
            <ViewerCount gameName={name} />
          </div>

          {/* ── Stat pills ── */}
          <div className="flex overflow-x-auto gap-2 mt-4 pb-1 hide-scroll-bar">
            <StatPill label="RATING">
              <span className="text-white semiBold text-base">{rate ?? "—"}</span>
            </StatPill>

            <StatPill label="DOWNLOADS">
              <span className="text-white semiBold text-base">{download ? `${download}K` : "—"}</span>
            </StatPill>

            <StatPill label="SIZE">
              <span className="text-white semiBold text-base">{size ?? "—"}</span>
            </StatPill>

            <StatPill label="DEVELOPER">
              <span className="text-white medium text-xs text-center leading-tight line-clamp-2">{author ?? "—"}</span>
            </StatPill>

            <StatPill label="PLATFORM">
              <div className="flex flex-row items-center gap-1.5 mt-0.5">
                {isForAndroid && <img src={androidIcon} className="h-4 w-4 opacity-80" alt="Android" />}
                {isForIos && <img src={appleIcon} className="h-4 w-4 opacity-80" alt="iOS" />}
              </div>
            </StatPill>

            <StatPill label="TYPE">
              <span className="pressStart text-light-blue" style={{ fontSize: "8px" }}>MOD</span>
            </StatPill>
          </div>

          {/* ── Description ── */}
          {description && (
            <div className="mt-6">
              <div
                className="w-1 h-4 rounded-full inline-block mr-2 align-middle"
                style={{ background: "linear-gradient(180deg, #468faf, #01497c)", verticalAlign: "middle" }}
              />
              <span className="text-white semiBold text-base align-middle">About</span>
              <p
                className={`medium text-sm mt-2 leading-relaxed ${expanded ? "" : "line-clamp-4"}`}
                style={{ color: "rgba(255,255,255,0.58)" }}
              >
                {description}
              </p>
              <button
                className="medium text-xs mt-1"
                style={{ color: "#468faf" }}
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? "Show less" : "Read more"}
              </button>
            </div>
          )}

          {/* ── Community section ── */}
          <div className="mt-7">
            <div className="flex items-center gap-2 mb-3">
              <div
                className="w-1 h-4 rounded-full flex-shrink-0"
                style={{ background: "linear-gradient(180deg, #468faf, #01497c)" }}
              />
              <span className="text-white semiBold text-base">Community</span>
              <span
                className="medium text-xs px-2 py-0.5 rounded-full"
                style={{
                  color: "#89d4f5",
                  background: "rgba(70,143,175,0.15)",
                  border: "1px solid rgba(70,143,175,0.2)",
                  fontSize: "10px",
                }}
              >
                {allComments.length} reviews
              </span>
            </div>

            {visibleComments.map((c, i) => (
              <div
                key={i}
                className="p-3 rounded-2xl mb-2.5"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.07)",
                }}
              >
                {/* Top row: avatar + name + stars + date */}
                <div className="flex flex-row items-center gap-2.5">
                  {/* Avatar */}
                  <div
                    className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: avatarColor(c.author) }}
                  >
                    <span className="text-white semiBold" style={{ fontSize: "13px" }}>
                      {c.author[0].toUpperCase()}
                    </span>
                  </div>
                  <div className="flex flex-col flex-1 min-w-0">
                    <div className="flex flex-row items-center justify-between">
                      <span className="text-white semiBold text-sm truncate">{c.author}</span>
                      <span className="text-gray-500 medium flex-shrink-0 ml-2" style={{ fontSize: "11px" }}>{c.date}</span>
                    </div>
                    <StarRating stars={c.stars} />
                  </div>
                </div>
                {/* Comment text */}
                <p
                  className="medium text-sm mt-2 leading-relaxed"
                  style={{ color: "rgba(255,255,255,0.58)" }}
                >
                  {c.text}
                </p>
              </div>
            ))}

            <button
              className="w-full text-center medium py-2"
              style={{ color: "#468faf", fontSize: "13px" }}
              onClick={() => setCommentsExpanded(!commentsExpanded)}
            >
              {commentsExpanded ? "Show less" : `Show all ${allComments.length} reviews`}
            </button>
          </div>

          {/* ── Sponsored section ── */}
          {numbers.length > 0 && (
            <div className="mt-7">
              <div className="flex items-center gap-2 mb-3">
                <div
                  className="w-1 h-4 rounded-full"
                  style={{ background: "linear-gradient(180deg, #f59e0b, #dc2626)" }}
                />
                <span className="text-white semiBold text-base">Sponsored</span>
              </div>

              {numbers.map((item, index) => (
                <div
                  key={index}
                  className="flex flex-row items-center gap-3 p-3 rounded-2xl mb-2.5"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.07)",
                  }}
                >
                  <div className="h-16 w-16 rounded-2xl overflow-hidden flex-none bg-gray-800">
                    <LazyLoadImage
                      className="h-16 w-16 object-cover object-center"
                      src={GET_BASE_URL() + "images/" + item.image_path}
                      alt={item.name}
                    />
                  </div>
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-white semiBold text-base truncate">{item.name}</span>
                    <div className="flex flex-row items-center gap-1.5 mt-1">
                      {item.isForAndroid && <img src={androidIcon} className="h-3.5 w-3.5 opacity-60" alt="Android" />}
                      {item.isForIos && <img src={appleIcon} className="h-3.5 w-3.5 opacity-60" alt="iOS" />}
                    </div>
                  </div>
                  <span
                    className="medium text-xs px-2 py-0.5 rounded-md"
                    style={{
                      background: "rgba(70,143,175,0.18)",
                      color: "#89d4f5",
                      border: "1px solid rgba(70,143,175,0.25)",
                      fontSize: "10px",
                    }}
                  >
                    Ad
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {showPreparing && (
          <PrepareNotification
            gameName={name}
            imageUrl={image_path}
            onComplete={() => { setShowPreparing(false); setShowOffers(true); }}
            onCancel={() => setShowPreparing(false)}
          />
        )}

        {showOffers && (
          <OffersScreen
            platform={platform}
            imageUrl={image_path}
            nicheName={name}
            size={size}
            skipStepper={true}
            onClose={handleClose}
          />
        )}
      </div>
    </>
  );
};

export default DetailsPage;
